import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api.service';
import { Observable, of } from 'rxjs';
import {
    HubspotCompanyDuplicate,
    HubspotEnrichmentJobsResponseDto,
    HubspotCompanyExport,
    HubspotCompanyRequest,
    HubspotStatusDto,
    HubspotWorkflowDto,
    HubspotEnrichmentJobsRequestDto,
    HubspotMappingFieldsByItemTypeResponseDto,
    HubspotCrmFieldMappingsResponseDto,
    HubspotCrmFieldMappingsRequestDto,
    HubspotCrmSettingsResponseDto,
    HubspotCrmSettingsDto,
    HubspotAutomationStatusDto,
    HubspotEnrichmentSearchByFiltersRequestDto,
    HubspotListsResponseDto,
    HubspotEnrichmentManualJobRequestDto,
    HubspotSearchCompanyResponseDto,
    HubspotSearchContactResponseDto,
    HubspotEnrichmentSaveScheduledEnrichmentJobRequest,
    HubspotGetScheduledEnrichmentResponseDto,
    HubspotEnrichmentReportingJobResponse,
    HubSpotEnrichmentBuyingCenterReportingResponse,
    LifecycleStageDto,
    HubspotEnrichmentJobFilterParametersDto,
    HubspotExportEnrichmentReportResponse,
    HubspotEnrichmentTriggeredJobResponseDto, HubspotEnrichmentTriggeredJobRequestDto,
    MappingFieldsRequestParamDto,
} from '../../dto';
import { CrmAppEnum, HubspotEnrichmentItemTypeEnum, ItemTypeValueType } from '../../enums';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class HubspotApiService {
    constructor(private readonly baseApiService: BaseApiService) { }

    authenticate(code: string, hsCrmValue?: boolean): Observable<any> {
        const params = {
          code: code,
          redirect_uri: environment.hubspot.redirectUrl
        };
        if (hsCrmValue !== undefined) {
          params['hs_crm_status'] = hsCrmValue;
        }
        return this.baseApiService.post('hubspot/authenticate', {}, { params });
    }

    refreshToken(): any {
        return this.baseApiService.post(`hubspot/refreshToken`);
    }

    logout(): Observable<any> {
      return this.baseApiService.get(`hubspot/logout`);
    }

    authenticateFilters(params: { code: string, redirect_uri?: string }): Observable<any> {
        return this.baseApiService.post('hubspot/filters/authenticate', null, { params });
    }

    refreshTokenFilters(): Observable<any> {
        return this.baseApiService.post(`hubspot/filters/refreshToken`);
    }

    logoutFilters(): Observable<any> {
        return this.baseApiService.get(`hubspot/filters/logout`);
    }

    saveSettings(payload: any): Observable<any> {
        return this.baseApiService.put('service/contactcloud/hubspot/settings', payload);
    }

    getMappingFields(params: MappingFieldsRequestParamDto = {}): Observable<any> {
        const requestParams = params.isAutomation ? { is_automation: true } : {};
        return this.baseApiService.get(`service/contactcloud/hubspot/fields`, { params: requestParams });
    }

    loadLeadOwner(): Observable<any> {
        return this.baseApiService.get('hubspot/leadowners');
    }

    loadWorkflows(showError: boolean): Observable<HubspotWorkflowDto[]> {
        return this.baseApiService.get('hubspot/workflows', { showError });
    }

    getLifecycleStages(): Observable<LifecycleStageDto[]> {
        return this.baseApiService.get<LifecycleStageDto[]>('service/contactcloud/hubspot/lifecycleStages');
    }

    validateDuplicates(exportType: string, ids: any[], icpIntelJobUUID: string) {
        return this.baseApiService.post('service/contactcloud/hubspot/duplicate', {
            exportType,
            icpIntelJobUUID,
            ids,
        });
    }

    validateDuplicateCompanies(validateCompaniesData: HubspotCompanyRequest): Observable<HubspotCompanyDuplicate> {
        return this.baseApiService.post('service/contactcloud/hubspot/companies/duplicate', validateCompaniesData);
    }

    export(exportType: string, leadOwner: string, workflowIds: number[], duplicateResolution: string, ids, icpIntelJobUUID: string) {
        return this.baseApiService.post('service/contactcloud/hubspot/export', {
            duplicateResolution: duplicateResolution === 'skip' ? '' : duplicateResolution,
            exportType,
            ids,
            workflowIds,
            leadOwner,
            icpIntelJobUUID
        });
    }

    exportCompanies(exportCompaniesData: HubspotCompanyRequest): Observable<HubspotCompanyExport> {
        return this.baseApiService.post('service/contactcloud/hubspot/companies/export', exportCompaniesData);
    }

    getScopes(): Observable<any> {
        return this.baseApiService.get('hubspot/scopes');
    }

    getSettings(): Observable<any> {
        return this.baseApiService.get('service/contactcloud/hubspot/settings');
    }

    getExportCrmSettings(crmName: string): Observable<any> {
        return this.baseApiService.get('service/contactcloud/techIntel/crm/settings', { params: { crmName } });
    }

    saveExportCrmSettings(type: string, settings): Observable<any> {
        return this.baseApiService.put('service/contactcloud/techIntel/crm/settings?crmName=' + type, settings);
    }

    validateMapping() {
        return this.baseApiService.get('service/contactcloud/hubspot/validateMapping');
    }

    checkAutomations(): Observable<HubspotAutomationStatusDto> {
        return this.baseApiService.get<HubspotAutomationStatusDto>('service/contactcloud/automation/status/hubspot');
    }

    disableAutomations(): Observable<void> {
        return this.baseApiService.put<void>('service/contactcloud/automation/hubspot/disable');
    }

    getEnrichmentJobsList(request: HubspotEnrichmentJobsRequestDto): Observable<HubspotEnrichmentJobsResponseDto> {
        return this.baseApiService.get('service/contactcloud/hubspot/v2/enrich', { params: { ...request } });
    }

    authenticateEnrichment(params: { code: string, crmApp?: CrmAppEnum, redirect_uri?: string }): Observable<any> {
        return this.baseApiService.post('service/contactcloud/crm/v1/hubspot/authenticate', null, { params });
    }

    logoutEnrichment(crmApp: CrmAppEnum = CrmAppEnum.ENRICH): Observable<any> {
        return this.baseApiService.post('service/contactcloud/crm/v1/hubspot/logout', null, { params: { crmApp }, showError: true });
    }

    getStatus(crmApp: CrmAppEnum = CrmAppEnum.ENRICH): Observable<HubspotStatusDto> {
        return this.baseApiService.get(`service/contactcloud/crm/v1/hubspot/status`, { params: { crmApp }, showError: true });
    }

    getCrmFields(itemType: HubspotEnrichmentItemTypeEnum, crmApp: CrmAppEnum = CrmAppEnum.ENRICH): Observable<HubspotMappingFieldsByItemTypeResponseDto> {
        return this.baseApiService.get(`service/contactcloud/crm/v1/hubspot/fields/${itemType}`, { params: { crmApp }, showError: true });
    }

    getCrmFieldMappings(crmApp: CrmAppEnum.EXPORT | CrmAppEnum.ENRICH = CrmAppEnum.ENRICH): Observable<HubspotCrmFieldMappingsResponseDto> {
        return this.baseApiService.get(`service/contactcloud/crm/v1/hubspot/field-mappings`, { params: { crmApp }, showError: true });
    }

    saveCrmFieldMappings(body: HubspotCrmFieldMappingsRequestDto, crmApp: CrmAppEnum.EXPORT | CrmAppEnum.ENRICH = CrmAppEnum.ENRICH): Observable<{ isSaved: true }> {
        return this.baseApiService.post('service/contactcloud/crm/v1/hubspot/field-mappings', body, { params: { crmApp }, showError: true, showSuccess: true });
    }

    getHubspotCrmSettings(crmApp: CrmAppEnum.EXPORT | CrmAppEnum.ENRICH = CrmAppEnum.ENRICH): Observable<HubspotCrmSettingsResponseDto> {
        return this.baseApiService.get<HubspotCrmSettingsResponseDto>('service/contactcloud/crm/v1/hubspot/settings', { params: { crmApp }, showError: true });
    }

    saveHubspotCrmSettings(hubspotCrmSettings: HubspotCrmSettingsDto, crmApp: CrmAppEnum.EXPORT | CrmAppEnum.ENRICH = CrmAppEnum.ENRICH): Observable<{ isSaved: true }> {
        return this.baseApiService.post('service/contactcloud/crm/v1/hubspot/settings', { hubspotCrmSettings }, { params: { crmApp }, showError: true });
    }

    getHubspotListsByType(type: HubspotEnrichmentItemTypeEnum, crmApp: CrmAppEnum.EXPORT | CrmAppEnum.ENRICH = CrmAppEnum.ENRICH, query?: string,): Observable<HubspotListsResponseDto> {
       return this.baseApiService.get(`service/contactcloud/crm/v1/hubspot/lists/${type}`, { params: { crmApp, query }, showError: true });
    }

    getHubspotDealLists(query?: string): Observable<HubspotListsResponseDto> {
      return this.baseApiService.get(`service/contactcloud/crm/v1/hubspot/lists/deal`, { params: { crmApp: CrmAppEnum.ENRICH_BCE, query }, showError: true });
    }

    getTriggerJobSettings(): Observable<HubspotEnrichmentTriggeredJobResponseDto> {
        return this.baseApiService.get('service/contactcloud/hubspot/v2/triggered', { showError: true });
    }

    saveTriggerJobSettings(body: HubspotEnrichmentTriggeredJobRequestDto) {
        return this.baseApiService.post('service/contactcloud/hubspot/v2/triggered', body, { showError: true });
    }

    deleteTriggerJobSettings(itemType: HubspotEnrichmentItemTypeEnum, showSuccess: boolean = true) {
        return this.baseApiService.delete('service/contactcloud/hubspot/v2/triggered', { params: { itemType }, showError: true, showSuccess });
    }

    createManualEnrichment(data: HubspotEnrichmentManualJobRequestDto): Observable<{ isCreated: boolean, message: string }> {
        return this.baseApiService.post('service/contactcloud/hubspot/v2/enrich', data, { showError: true });
    }

    getSearchContact(data: HubspotEnrichmentSearchByFiltersRequestDto, crmApp: CrmAppEnum.EXPORT | CrmAppEnum.ENRICH = CrmAppEnum.ENRICH): Observable<HubspotSearchContactResponseDto> {
        return this.baseApiService.post('service/contactcloud/crm/v1/hubspot/search/contact', data, { params: { crmApp }, showError: true });
    }

    getSearchCompany(data: HubspotEnrichmentSearchByFiltersRequestDto, crmApp: CrmAppEnum.EXPORT | CrmAppEnum.ENRICH = CrmAppEnum.ENRICH): Observable<HubspotSearchCompanyResponseDto> {
        return this.baseApiService.post('service/contactcloud/crm/v1/hubspot/search/company', data, { params: { crmApp }, showError: true });
    }

    getSearchDeal(data: HubspotEnrichmentSearchByFiltersRequestDto, crmApp: CrmAppEnum = CrmAppEnum.ENRICH_BCE): Observable<HubspotSearchCompanyResponseDto> {
        return this.baseApiService.post('service/contactcloud/crm/v1/hubspot/search/deal', data, { params: { crmApp }, showError: true });
    }

    getScheduledEnrichment(): Observable<HubspotGetScheduledEnrichmentResponseDto> {
        return this.baseApiService.get('service/contactcloud/hubspot/v2/schedule')
    }

    saveScheduledEnrichment(data: HubspotEnrichmentSaveScheduledEnrichmentJobRequest): Observable<{ isCreated: boolean, message: string }> {
        return this.baseApiService.post('service/contactcloud/hubspot/v2/schedule', data, { showError: true });
    }

    deleteScheduledEnrichment(itemType: HubspotEnrichmentItemTypeEnum, showSuccess: boolean = true): Observable<{ isDeleted: boolean }> {
        return this.baseApiService.delete('service/contactcloud/hubspot/v2/schedule', { params: { itemType }, showError: true, showSuccess });
    }

    rollbackEnrichmentJob(enrichmentJobId: string): Observable<{ isCreated: boolean, message: string }> {
        return this.baseApiService.post(`service/contactcloud/hubspot/v2/rollback/${enrichmentJobId}`, null, { showError: true, showSuccess: true });
    }

    exportEnrichmentReport(enrichmentJobId: string): Observable<HubspotExportEnrichmentReportResponse> {
        return this.baseApiService.get(`service/contactcloud/hubspot/v2/enrich/${enrichmentJobId}/export-report`, { showError: true, showSuccess: true });
    }

    getEnrichmentJobFilterParameters(enrichmentJobId: string): Observable<HubspotEnrichmentJobFilterParametersDto> {
        return this.baseApiService.get<{ enrichmentJob: HubspotEnrichmentJobFilterParametersDto }>(`service/contactcloud/hubspot/v2/enrich/${enrichmentJobId}`, { showError: true })
            .pipe(map(({ enrichmentJob }: { enrichmentJob: HubspotEnrichmentJobFilterParametersDto }) => enrichmentJob));
    }

    getEnrichmentBuyingCenterReport(enrichmentJobId: string): Observable<HubSpotEnrichmentBuyingCenterReportingResponse> {
        return this.baseApiService.get(`service/contactcloud/hubspot/v2/enrich/${enrichmentJobId}/report/buying-centre`, { showError: true });
    }

    getEnrichmentReportJob(enrichmentJobId: string): Observable<HubspotEnrichmentReportingJobResponse> {
        return this.baseApiService.get(`service/contactcloud/hubspot/v2/enrich/${enrichmentJobId}/report`, { showError: true });
    }

    postEnrichmentReportApprove(enrichmentJobId: string): Observable<HubspotEnrichmentReportingJobResponse> {
        return this.baseApiService.post(`service/contactcloud/hubspot/v2/enrich/${enrichmentJobId}/report/approve`, null, { showError: true, showSuccess: true });
    }
}
